// frontend/src/services/api.js - CLEANED VERSION (REMOVE BACKEND CODE)
import { ensureSocket } from "./socketService";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

console.log('🌐 API configured for:', API_URL);

// Enhanced helper function for API requests with better error handling
const apiRequest = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  
  console.log(`🔗 API Request: ${API_URL}${url}`);
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else if (url.includes('/admin/')) {
    console.warn('⚠️ No auth token found - admin requests may fail');
  }
  
  // Enhanced fetch with timeout and error handling
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), url.includes('/admin/') ? 30000 : 20000);
  
  try {
    const response = await fetch(`${API_URL}${url}`, {
      ...options,
      headers,
      credentials: 'include',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    console.log(`📡 Response status: ${response.status} for ${url}`);
    
    let data;
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const textData = await response.text();
      console.warn('⚠️ Non-JSON response received:', textData.substring(0, 200));
      data = { success: false, error: 'Invalid response format', rawResponse: textData };
    }
    
    if (!response.ok) {
      console.error('❌ API Error:', data);
      const errorMessage = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
      
      // Special handling for authentication errors
      if (response.status === 401) {
        console.error('🔐 Authentication failed');
        if (url.includes('/admin/')) {
          console.error('🔐 Admin authentication failed - redirecting to login');
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
      } else if (response.status === 403) {
        console.error('🚫 Access forbidden');
        if (url.includes('/admin/')) {
          throw new Error('Admin access required. Please ensure you have admin privileges.');
        }
      }
      
      throw new Error(errorMessage);
    }
    
    console.log('✅ API Success:', data);
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    console.error('❌ Request failed:', error);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please check your connection and try again.');
    } else if (error.message === 'Failed to fetch') {
      throw new Error('Unable to connect to server. Please check your connection.');
    }
    
    throw error;
  }
};

const api = {
  // Auth endpoints
  register: async (userData) => {
    console.log('📝 Registering user:', userData.email);
    
    const registrationData = {
      firstName: userData.firstName.trim(),
      lastName: userData.lastName.trim(),
      email: userData.email.toLowerCase().trim(),
      password: userData.password,
      youtubeChannel: userData.youtubeChannel?.trim() || ''
    };
    
    const response = await apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(registrationData)
    });
    
    if (response.success && response.token) {
      localStorage.setItem('token', response.token);
      console.log('💾 Token saved');
    }
    
    return response;
  },

  login: async (credentials) => {
    console.log('🔑 Logging in:', credentials.email);
    
    const loginData = {
      email: credentials.email.toLowerCase().trim(),
      password: credentials.password
    };
    
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify(loginData)
    });
    
    if (response.success && response.token) {
      localStorage.setItem('token', response.token);
      console.log('💾 Token saved');

      try {
        const socket = await ensureSocket();
        if (response.data?._id || response.data?.id) {
          socket.emit("joinUserRoom", response.data._id || response.data.id);
          console.log("📡 Joined user room:", response.data._id || response.data.id);
        }
      } catch (err) {
        console.error("⚠️ Socket connection failed:", err);
      }
    }
    
    return response;
  },

  getCurrentUser: async () => {
    console.log('👤 Getting current user');
    return apiRequest('/auth/me');
  },

  logout: async () => {
    console.log('🚪 Logging out');
    try {
      await apiRequest('/auth/logout');
    } catch (error) {
      console.warn('Logout API call failed, clearing local token anyway');
    }
    localStorage.removeItem('token');
  },

  // Render endpoints
  createRenderJob: async (jobData) => {
    console.log('➕ Creating render job:', jobData.name);
    return apiRequest('/renders', {
      method: 'POST',
      body: JSON.stringify(jobData)
    });
  },

  getRenderJob: async (id) => {
    console.log('🎬 Getting render job:', id);
    return apiRequest(`/renders/${id}`);
  },

  updateRenderJob: async (id, updateData) => {
    console.log('📝 Updating render job:', id);
    return apiRequest(`/renders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  },

  getQueueStatus: async () => {
    console.log('📊 Getting queue status');
    try {
      return await apiRequest('/renders/queue');
    } catch (error) {
      console.error('❌ Queue status error:', error);
      return {
        success: true,
        data: {
          express: [],
          priority: [],
          standard: [],
          economy: [],
          processing: [],
          counts: { express: 0, priority: 0, standard: 0, economy: 0, processing: 0, total: 0 },
          isPaused: false,
          backend: 'Error'
        }
      };
    }
  },

  getRenderOutput: async (id) => {
    console.log('⬇️ Getting render output:', id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/renders/${id}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json, video/mp4, */*'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || `HTTP ${response.status}`;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        return data;
      } else {
        const blob = await response.blob();
        const contentDisposition = response.headers.get('content-disposition');
        let filename = `render_${id}.mp4`;

        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (matches && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true, type: 'direct_download', filename };
      }
    } catch (error) {
      console.error('❌ Download failed:', error);
      throw new Error(error.message);
    }
  },

  // File endpoints
  uploadBlendFile: (file, onProgress) => {
    const token = localStorage.getItem('token');
    console.log('📤 Uploading blend file:', file.name, `(${(file.size / 1024 / 1024).toFixed(1)}MB)`);

    const UPLOAD_TIMEOUT = 600000; // 10 minutes for large files
    const MAX_RETRIES = 2;

    const attemptUpload = (retryCount = 0) => {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);

        const xhr = new XMLHttpRequest();
        let timedOut = false;

        const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
        const timeoutId = setTimeout(() => {
          timedOut = true;
          xhr.abort();
          reject(new Error(`Upload timed out after 10 minutes (${fileSizeMB}MB file). Your connection may be too slow or the storage server may be unavailable.`));
        }, UPLOAD_TIMEOUT);

        // Track upload progress
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            const percent = Math.round((event.loaded / event.total) * 100);
            onProgress(percent);
          }
        });

        xhr.addEventListener('load', () => {
          clearTimeout(timeoutId);
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(data);
            } else {
              // Map server errors to user-friendly messages
              let errorMsg = data.error || 'Upload failed';
              if (xhr.status === 413 || errorMsg.toLowerCase().includes('too large')) {
                errorMsg = 'File exceeds 500MB limit. Please reduce file size and try again.';
              } else if (xhr.status === 415 || errorMsg.toLowerCase().includes('file type')) {
                errorMsg = 'Invalid file format. Please upload a .blend file.';
              } else if (xhr.status === 503) {
                errorMsg = 'MinIO storage service is unavailable. Please try again later or contact support.';
              } else if (xhr.status === 504) {
                errorMsg = 'Upload timed out. The file may be too large or your connection is slow. Please try again.';
              }
              reject(new Error(errorMsg));
            }
          } catch (e) {
            reject(new Error('Invalid response from server'));
          }
        });

        xhr.addEventListener('error', () => {
          clearTimeout(timeoutId);
          if (!timedOut && retryCount < MAX_RETRIES) {
            console.log(`⚠️ Upload failed, retrying (${retryCount + 1}/${MAX_RETRIES})...`);
            setTimeout(() => {
              attemptUpload(retryCount + 1).then(resolve).catch(reject);
            }, 2000 * (retryCount + 1)); // Exponential backoff
          } else {
            reject(new Error('Unable to connect to server. Please check your connection.'));
          }
        });

        xhr.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          if (!timedOut) {
            reject(new Error('Upload cancelled'));
          }
        });

        xhr.open('POST', `${API_URL}/files/upload`);
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.withCredentials = true;
        xhr.send(formData);
      });
    };

    return attemptUpload();
  },

  // User endpoints
  updateUserProfile: async (profileData) => {
    console.log('📝 Updating user profile');
    return apiRequest('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  },

  updatePassword: async (passwordData) => {
    console.log('🔒 Updating password');
    return apiRequest('/users/password', {
      method: 'PUT',
      body: JSON.stringify(passwordData)
    });
  },

  getUserUsage: async () => {
    console.log('📊 Getting user usage');
    return apiRequest('/users/usage');
  },

  // Payment endpoints
  createPaymentRecord: async (paymentData) => {
    console.log('💳 Creating payment record:', paymentData);
    return apiRequest('/payments/create-record', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  },

  createCountryQRPayment: async (paymentData) => {
    console.log('🏷️ Creating QR payment record:', paymentData);
    return apiRequest('/payments/create-qr', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  },

  createUSDTPayment: async (paymentData) => {
    console.log('💰 Creating USDT payment record:', paymentData);
    return apiRequest('/payments/create-usdt', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  },

  submitUSDTTransaction: async (paymentId, transactionHash) => {
    console.log('📝 Submitting USDT transaction:', paymentId);
    return apiRequest(`/payments/submit-transaction/${paymentId}`, {
      method: 'PUT',
      body: JSON.stringify({ transactionHash })
    });
  },

  getPaymentStatus: async (paymentId) => {
    console.log('📊 Getting payment status:', paymentId);
    return apiRequest(`/payments/${paymentId}/status`);
  },

  // "I Paid" – notify admin via Telegram for approval
  iPaid: async (paymentId) => {
    console.log('💸 I-Paid notification for:', paymentId);
    return apiRequest(`/payments/${paymentId}/i-paid`, { method: 'POST' });
  },

  // ✅ ADD THIS NEW METHOD for MultiCryptoPayment
  updatePaymentRecord: async (paymentId, updateData) => {
  console.log('📝 Updating payment record:', paymentId, updateData);
  return apiRequest(`/payments/${paymentId}/update`, {
    method: 'PUT',
    body: JSON.stringify(updateData)
  });
},

  // Render methods
  getRenderQueue: async () => {
    console.log('📋 Getting render queue');
    return apiRequest('/renders');
  },

  getAllRenderJobs: async (params = {}) => {
    console.log('📋 Getting all render jobs');
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/renders?${queryString}` : '/renders';
    return apiRequest(url);
  },

  getRenderPreview: async (id) => {
    console.log('👁️ Getting render preview:', id);
    return apiRequest(`/renders/${id}/preview`);
  },

  downloadRenderFile: async (id, filename) => {
    console.log('📥 Starting download for render:', id);
    const result = await api.getRenderOutput(id);

    if (result.type === 'direct_download') {
      return result;
    } else if (result.success && result.data?.downloadUrl) {
      const link = document.createElement('a');
      link.href = result.data.downloadUrl;
      link.download = filename || result.data.fileName || `render_${id}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return { success: true, type: 'signed_url' };
    } else {
      throw new Error('Invalid response format');
    }
  },

  getQueueStatistics: async () => {
    console.log('📈 Getting queue statistics');
    return apiRequest('/renders/queue/statistics');
  },

  pauseQueue: async () => {
    console.log('⏸️ Pausing queue');
    return apiRequest('/renders/queue/pause', { method: 'POST' });
  },

  resumeQueue: async () => {
    console.log('▶️ Resuming queue');
    return apiRequest('/renders/queue/resume', { method: 'POST' });
  },

  // File methods
  getFile: async (id) => {
    console.log('📁 Getting file:', id);
    return apiRequest(`/files/${id}`);
  },

  deleteFile: async (id) => {
    console.log('🗑️ Deleting file:', id);
    return apiRequest(`/files/${id}`, { method: 'DELETE' });
  },

  getRenderFile: async (renderId) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/files/render/${renderId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to fetch render');
    return res.blob();
  },

  // User methods
  getUserProfile: async () => {
    console.log('👤 Getting user profile');
    return apiRequest('/users/profile');
  },

  // Auth methods
  forgotPassword: async (email) => {
    console.log('🔐 Forgot password for:', email);
    return apiRequest('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  },

  resetPassword: async (token, password) => {
    console.log('🔑 Resetting password');
    return apiRequest(`/auth/reset-password/${token}`, {
      method: 'PUT',
      body: JSON.stringify({ password })
    });
  },

  // Subscription methods
  getSubscriptionPlans: async () => {
    console.log('💳 Getting subscription plans');
    return apiRequest('/subscriptions/plans');
  },

  createSubscription: async (subscriptionData) => {
    console.log('💳 Creating subscription');
    return apiRequest('/subscriptions', {
      method: 'POST',
      body: JSON.stringify(subscriptionData)
    });
  },

  getCurrentSubscription: async () => {
    console.log('💳 Getting current subscription');
    return apiRequest('/subscriptions/current');
  },

  cancelSubscription: async () => {
    console.log('❌ Cancelling subscription');
    return apiRequest('/subscriptions/cancel', { method: 'PUT' });
  },

  resumeSubscription: async () => {
    console.log('▶️ Resuming subscription');
    return apiRequest('/subscriptions/resume', { method: 'PUT' });
  },

  // Test connection
  testConnection: async () => {
    console.log('🧪 Testing API connection...');
    try {
      const response = await fetch(`${API_URL.replace('/api', '')}/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      console.log('✅ API health check:', data);
      return data;
    } catch (error) {
      console.error('❌ API connection failed:', error);
      throw error;
    }
  }
};

const adminAPI = {
  getPendingPayments: async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/admin/payments/pending`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) throw new Error('Failed to fetch pending payments');
    return await response.json();
  },

  getAdminStats: async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/admin/stats`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) throw new Error('Failed to fetch admin stats');
    return await response.json();
  },

  verifyPayment: async (paymentId, verificationData) => {
    const token = localStorage.getItem('token');
    console.log('🔍 Admin verifying payment:', { paymentId, verificationData });
    
    const response = await fetch(`${API_URL}/admin/payments/${paymentId}/verify`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(verificationData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to verify payment (${response.status})`);
    }
    
    const result = await response.json();
    console.log('✅ Payment verification result:', result);
    return result;
  },

  testAdminConnection: async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/admin/stats`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await response.json();
      return { connected: response.ok, status: response.status, data };
    } catch (error) {
      return { connected: false, error: error.message };
    }
  },
};

export { api, adminAPI };